import { useState, useCallback, useRef, useEffect } from 'react';
import { useErrorHandler } from '../services/errorHandler';
import * as yup from 'yup';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: any) => string | null;
  message?: string;
  messageAr?: string;
}

export interface ValidationRules {
  [fieldName: string]: ValidationRule;
}

export interface FormErrors {
  [fieldName: string]: string;
}

export interface FormState<T> {
  values: T;
  errors: FormErrors;
  isValid: boolean;
  isSubmitting: boolean;
  isDirty: boolean;
  touched: { [key: string]: boolean };
}

export interface UseFormOptions<T> {
  initialValues: T;
  validationRules?: ValidationRules;
  validationSchema?: yup.Schema<T>;
  onSubmit?: (values: T) => Promise<void> | void;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  resetOnSubmit?: boolean;
  onSuccess?: (values: T) => void;
}

export function useForm<T extends Record<string, any>>({
  initialValues,
  validationRules = {},
  validationSchema,
  onSubmit,
  validateOnChange = true,
  validateOnBlur = true,
  resetOnSubmit = false,
  onSuccess,
}: UseFormOptions<T>) {
  const errorHandler = useErrorHandler();
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const initialValuesRef = useRef<T>(initialValues);

  // Calculate derived state
  const isValid = Object.keys(errors).length === 0 && Object.values(values).some(v => v !== '' && v != null);
  const isDirty = JSON.stringify(values) !== JSON.stringify(initialValuesRef.current);

  // Validation function
  const validateField = useCallback((fieldName: string, value: any): string | null => {
    const rule = validationRules[fieldName];
    if (!rule) return null;

    // Required validation
    if (rule.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      return errorHandler.getLocalizedMessage('validation.required', rule.message || 'This field is required');
    }

    // Skip other validations if value is empty and not required
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return null;
    }

    // Min length validation
    if (rule.minLength && typeof value === 'string' && value.length < rule.minLength) {
      return errorHandler.getLocalizedMessage('validation.minLength', rule.message || `Must be at least ${rule.minLength} characters`, { min: rule.minLength });
    }

    // Max length validation
    if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
      return errorHandler.getLocalizedMessage('validation.maxLength', rule.message || `Must be less than ${rule.maxLength} characters`, { max: rule.maxLength });
    }

    // Pattern validation
    if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
      return errorHandler.getLocalizedMessage('validation.pattern', rule.message || 'Invalid format');
    }

    // Custom validation
    if (rule.custom) {
      return rule.custom(value);
    }

    return null;
  }, [validationRules, errorHandler]);

  // Validate all fields - supports both Yup schema and legacy validation rules
  const validateForm = useCallback(async (): Promise<FormErrors> => {
    const newErrors: FormErrors = {};
    
    // Use Yup schema if provided
    if (validationSchema) {
      try {
        await validationSchema.validate(values, { abortEarly: false });
        setErrors({});
        return {};
      } catch (err: any) {
        if (err.inner && Array.isArray(err.inner)) {
          err.inner.forEach((error: yup.ValidationError) => {
            if (error.path) {
              newErrors[error.path] = error.message;
            }
          });
        } else if (err.path) {
          newErrors[err.path] = err.message;
        }
        setErrors(newErrors);
        return newErrors;
      }
    }
    
    // Fallback to legacy validation rules
    Object.keys(validationRules).forEach(fieldName => {
      const error = validateField(fieldName, values[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });

    setErrors(newErrors);
    return newErrors;
  }, [values, validateField, validationRules, validationSchema]);

  // Validate single field - supports both Yup and legacy validation
  const validateSingleField = useCallback(async (fieldName: string) => {
    // Only validate if field has been touched
    if (!touched[fieldName]) return null;
    
    // Use Yup schema if provided
    if (validationSchema) {
      try {
        // Create a test schema for this single field
        const testSchema = yup.object().shape({
          [fieldName]: (validationSchema as any).fields?.[fieldName] || yup.mixed()
        });
        await testSchema.validateAt(fieldName, values);
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
        return null;
      } catch (err: any) {
        const errorMessage = err.message || 'Invalid value';
        setErrors(prev => ({
          ...prev,
          [fieldName]: errorMessage
        }));
        return errorMessage;
      }
    }
    
    // Fallback to legacy validation
    const error = validateField(fieldName, values[fieldName]);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error || ''
    }));
    return error;
  }, [values, validateField, touched, validationSchema]);

  // Handle input change
  const handleChange = useCallback((fieldName: string, value: any) => {
    setValues(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // Validate on change if enabled
    if (validateOnChange && touched[fieldName]) {
      setTimeout(() => validateSingleField(fieldName), 100);
    }
  }, [errors, validateOnChange, validateSingleField, touched]);

  // Handle input blur
  const handleBlur = useCallback((fieldName: string) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));

    // Validate on blur if enabled
    if (validateOnBlur) {
      validateSingleField(fieldName);
    }
  }, [validateOnBlur, validateSingleField]);

  // Handle form submission
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    // Mark all fields as touched
    const fieldsToTouch = validationSchema 
      ? Object.keys((validationSchema as any).fields || {})
      : Object.keys(validationRules);
    
    const allTouched = fieldsToTouch.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as { [key: string]: boolean });
    setTouched(allTouched);

    // Validate form
    const validationErrors = await validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      // Focus first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"], #${firstErrorField}, [aria-describedby*="${firstErrorField}"]`);
      if (errorElement && errorElement instanceof HTMLElement) {
        setTimeout(() => errorElement.focus(), 100);
      }
      return false;
    }

    if (onSubmit) {
      setIsSubmitting(true);
      try {
        await onSubmit(values);
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(values);
        }
        
        if (resetOnSubmit) {
          resetForm();
        }
        
        return true;
      } catch (error) {
        // Handle validation errors from backend
        if (error && typeof error === 'object' && 'response' in error) {
          const apiError = error as any;
          if (apiError.response?.data?.validationErrors) {
            const validationErrors = apiError.response.data.validationErrors;
            const newErrors: FormErrors = {};
            Object.keys(validationErrors).forEach((fieldName) => {
              const fieldErrors = validationErrors[fieldName];
              if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
                newErrors[fieldName] = fieldErrors[0];
              }
            });
            setErrors(prev => ({ ...prev, ...newErrors }));
          }
        }
        errorHandler.handleError(error, { showToast: true, showValidationErrors: false });
        return false;
      } finally {
        setIsSubmitting(false);
      }
    }

    return true;
  }, [values, validateForm, onSubmit, resetOnSubmit, onSuccess, errorHandler, validationRules, validationSchema]);

  // Reset form
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
    initialValuesRef.current = initialValues;
  }, [initialValues]);

  // Set field value programmatically
  const setFieldValue = useCallback((fieldName: string, value: any) => {
    handleChange(fieldName, value);
  }, [handleChange]);

  // Set field error programmatically
  const setFieldError = useCallback((fieldName: string, error: string) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  }, []);

  // Clear field error
  const clearFieldError = useCallback((fieldName: string) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: ''
    }));
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Set multiple field values
  const setMultipleValues = useCallback((newValues: Partial<T>) => {
    setValues(prev => ({
      ...prev,
      ...newValues
    }));
  }, []);

  // Get field props for input components
  const getFieldProps = useCallback((fieldName: string) => {
    return {
      value: values[fieldName] || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        handleChange(fieldName, e.target.value);
      },
      onBlur: () => handleBlur(fieldName),
      error: touched[fieldName] ? errors[fieldName] : undefined,
      'aria-invalid': touched[fieldName] && errors[fieldName] ? true : false,
      'aria-describedby': touched[fieldName] && errors[fieldName] ? `${fieldName}-error` : undefined,
    };
  }, [values, errors, touched, handleChange, handleBlur]);

  // Update initial values when they change
  useEffect(() => {
    initialValuesRef.current = initialValues;
  }, [initialValues]);

  return {
    // State
    values,
    errors,
    isValid,
    isSubmitting,
    isDirty,
    touched,
    
    // Actions
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    validateForm,
    validateSingleField,
    
    // Field management
    setFieldValue,
    setFieldError,
    clearFieldError,
    clearErrors,
    setMultipleValues,
    getFieldProps,
  };
}

// Predefined validation rules
export const validationRules = {
  email: {
    required: true,
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    message: 'Please enter a valid email address',
    messageAr: 'يرجى إدخال عنوان بريد إلكتروني صحيح'
  },
  password: {
    required: true,
    minLength: 6,
    maxLength: 100
  },
  name: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[a-zA-Z\u0600-\u06FF\s]{2,50}$/,
    message: 'Name must be at least 2 characters long',
    messageAr: 'يجب أن يتكون الاسم من حرفين على الأقل'
  },
  phone: {
    pattern: /^[\+]?[0-9\s\-\(\)]{7,20}$/,
    message: 'Please enter a valid phone number',
    messageAr: 'يرجى إدخال رقم هاتف صحيح'
  },
  required: {
    required: true,
    message: 'This field is required',
    messageAr: 'هذا الحقل مطلوب'
  },
  title: {
    required: true,
    minLength: 3,
    maxLength: 200,
    message: 'Title is required',
    messageAr: 'العنوان مطلوب'
  },
  description: {
    required: true,
    minLength: 10,
    maxLength: 1000,
    message: 'Description is required',
    messageAr: 'الوصف مطلوب'
  },
  price: {
    required: true,
    custom: (value: string) => {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return 'Please enter a valid price';
      if (numValue < 0) return 'Price must be a positive number';
      return null;
    }
  },
  slug: {
    required: true,
    pattern: /^[a-z0-9\-]+$/,
    message: 'URL slug contains invalid characters',
    messageAr: 'معرف الرابط يحتوي على أحرف غير صالحة'
  }
};

export default useForm;
